import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, LogOut, Mail, Phone, TrendingUp, Users, MousePointerClick, FileText, Eye, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { adminLogin, getSubmissions, getAnalyticsSummary, updateSubmissionStatus } from '../api';
import { toast } from 'sonner';

const STORAGE_KEY = 'adl_admin_token';

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEY) || '');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const loadData = async (authToken) => {
    try {
      const [subs, stats] = await Promise.all([
        getSubmissions(authToken),
        getAnalyticsSummary(authToken),
      ]);
      setSubmissions(subs);
      setAnalytics(stats);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please log in again.');
      }
    }
  };

  useEffect(() => {
    if (token) {
      loadData(token);
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const result = await adminLogin(password);
      if (result.success) {
        localStorage.setItem(STORAGE_KEY, result.token);
        setToken(result.token);
        toast.success('Welcome back, Melinda!');
      }
    } catch (error) {
      toast.error('Invalid password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken('');
    setPassword('');
    setSubmissions([]);
    setAnalytics(null);
  };

  const handleStatusToggle = async (sub) => {
    const newStatus = sub.status === 'new' ? 'contacted' : 'new';
    try {
      await updateSubmissionStatus(sub.id, newStatus, token);
      setSubmissions((prev) =>
        prev.map((s) => (s.id === sub.id ? { ...s, status: newStatus } : s))
      );
      toast.success(`Marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // ==================== LOGIN SCREEN ====================
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-teal-900 to-cyan-900 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="bg-white/5 backdrop-blur-md rounded-3xl p-10 border border-white/10 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Lock className="w-8 h-8 text-teal-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
              <p className="text-white/60">ADL Cleaning Services</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="password" className="text-white mb-2 block">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="admin-password-input"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-teal-400 h-12"
                />
              </div>
              <Button
                type="submit"
                data-testid="admin-login-button"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white h-12 text-lg font-semibold"
              >
                {isLoading ? 'Logging in...' : 'Log In'}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // ==================== DASHBOARD ====================
  const statCards = analytics ? [
    { label: 'Total Requests', value: analytics.totalSubmissions, icon: FileText, color: 'teal' },
    { label: 'New (Unread)', value: analytics.newSubmissions, icon: Sparkles, color: 'cyan' },
    { label: 'Page Views', value: analytics.pageViews, icon: Eye, color: 'blue' },
    { label: 'CTA Clicks', value: analytics.ctaClicks, icon: MousePointerClick, color: 'teal' },
    { label: 'Form Submits', value: analytics.formSubmits, icon: Users, color: 'cyan' },
    { label: 'Conversion Rate', value: `${analytics.conversionRate}%`, icon: TrendingUp, color: 'blue' },
  ] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
              ADL Admin Dashboard
            </h1>
            <p className="text-gray-500 text-sm">Quote requests & analytics</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 py-10">
        {/* Analytics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100"
                data-testid={`stat-card-${index}`}
              >
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-teal-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
              </motion.div>
            );
          })}
        </div>

        {/* Submissions */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Quote Requests</h2>
          <span className="text-gray-500">{submissions.length} total</span>
        </div>

        {submissions.length === 0 ? (
          <div className="bg-white rounded-2xl p-16 text-center border border-gray-100">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No quote requests yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="submissions-list">
            {submissions.map((sub, index) => (
              <motion.div
                key={sub.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
                data-testid={`submission-${index}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-bold text-gray-900">{sub.name}</h3>
                      <Badge
                        className={sub.status === 'new'
                          ? 'bg-cyan-100 text-cyan-700 hover:bg-cyan-100'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-100'}
                      >
                        {sub.status === 'new' ? 'New' : 'Contacted'}
                      </Badge>
                      {sub.serviceType && (
                        <Badge variant="outline" className="border-teal-200 text-teal-700 capitalize">
                          {sub.serviceType}
                        </Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                      <a href={`mailto:${sub.email}`} className="flex items-center gap-2 hover:text-teal-600">
                        <Mail className="w-4 h-4" />{sub.email}
                      </a>
                      {sub.phone && (
                        <a href={`tel:${sub.phone}`} className="flex items-center gap-2 hover:text-teal-600">
                          <Phone className="w-4 h-4" />{sub.phone}
                        </a>
                      )}
                    </div>

                    <p className="text-gray-700 bg-gray-50 rounded-xl p-4">{sub.message}</p>
                    <p className="text-xs text-gray-400 mt-3">{formatDate(sub.timestamp)}</p>
                  </div>

                  <Button
                    onClick={() => handleStatusToggle(sub)}
                    data-testid={`toggle-status-${index}`}
                    variant="outline"
                    className={sub.status === 'new'
                      ? 'border-teal-500 text-teal-600 hover:bg-teal-50 whitespace-nowrap'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50 whitespace-nowrap'}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {sub.status === 'new' ? 'Mark Contacted' : 'Mark New'}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
